import { IntlProvider } from 'react-intl';
import defaultMessages from '../../lang/default.json';
import { useState, useEffect, createContext } from 'react';
import { languageMap, availableLanguages, rtlLanguages } from './config';

// Create context with default values for better TypeScript/IDE support
export const LanguageContext = createContext({
  language: 'en-US',
  availableLanguages: [],
  changeLanguage: () => {}
});

export const IntlWrapper = ({ children }) => {
  const [language, setLanguage] = useState('');
  const [messages, setMessages] = useState(defaultMessages);

  // Select language from query string or fallback to browser language
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const uiLocales = urlParams.get('ui_locales') || '';
    const locale = uiLocales.split(' ')[0] || navigator.language || '';

    const normalizedLocale = locale.replace(/_/g, '-').toLowerCase();
    let detectedLanguage = 'en-US';
    const subtags = normalizedLocale.split('-');
    
    while (subtags.length) {
      const candidate = subtags.join('-');
      if (languageMap[candidate]) {
        detectedLanguage = languageMap[candidate];
        break;
      }
      subtags.pop();
    }
    
    setLanguage(detectedLanguage);
  }, []);

  // Load messages for language
  useEffect(() => {
    if (!language) return;

    import(`../../lang/${language}.json`)
      .then((loadedMessages) => {
        const resolvedMessages = loadedMessages.default || loadedMessages;
        setMessages(resolvedMessages);
        document.documentElement.dir = rtlLanguages.includes(language) ? 'rtl' : 'ltr';
      })
      .catch((error) => {
        console.error(`Failed to load language "${language}":`, error);
        // Fallback to default messages and still set direction
        setMessages(defaultMessages);
        document.documentElement.dir = rtlLanguages.includes(language) ? 'rtl' : 'ltr';
      });
  }, [language]);

  // Manage language changes
  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  // Prepare context value
  const contextValue = { language, availableLanguages, changeLanguage };

  return (
    <LanguageContext.Provider value={contextValue}>
      <IntlProvider locale={language || 'en'} messages={messages}>
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};

export default IntlWrapper;
